export default {
    title: 'AI Prompts',
    hint: 'Pick a prompt, copy it and paste it into your AI assistant.',
    empty: 'No prompts here',
    emptyText: 'The prompts on offer change with the screen you\'re on.',
    copy: 'Copy',
    copied: 'Copied!',
    triggerTooltip: 'AI Prompts',

    // Appended to the copied text only (hidden from the preview). The "Client"
    // variant is used when the screen has a client in context.
    copyContext: 'Use my Athlisis data.',
    copyContextClient: 'Use my Athlisis data for the client with ID {{athleteId}}.',

    contexts: {
        practice: {
            label: 'Schedule & clients',
            items: [
                {
                    slug: 'home-today',
                    title: 'Today at a glance',
                    body: 'Prepare me for today: appointments, clients, and anything I should watch out for.',
                },
                {
                    slug: 'home-week-overview',
                    title: 'Week overview',
                    body: 'Show me this week\'s appointments and which day is the most demanding.',
                },
                {
                    slug: 'appt-occupancy-compare',
                    title: 'Occupancy comparison',
                    body: 'How full is my schedule this week compared to last week?',
                },
                {
                    slug: 'appt-revenue',
                    title: 'Day / week revenue',
                    body: 'Based on the appointments, what is the total revenue today / this week?',
                },
                {
                    slug: 'home-week-priorities',
                    title: 'Week priorities',
                    body: 'Based on my appointments and clients this week, what are the 3 main priorities?',
                },
                {
                    slug: 'home-quick-prep',
                    title: 'Quick prep',
                    body: 'Show me a short summary for each client I have today: goals, latest measurement, notes.',
                },
                {
                    slug: 'appt-new-clients',
                    title: 'New-client appointments',
                    body: 'Which appointments this week are first sessions with new clients? What should I know and prepare?',
                },
                {
                    slug: 'home-new-client-briefing',
                    title: 'New client briefing',
                    body: 'I have a new client coming in today — show me their profile and what to ask in the first session.',
                },
                {
                    slug: 'appt-session-prep',
                    title: 'Session prep',
                    body: 'Prepare me for tomorrow\'s appointments: each client\'s measurements, plan, and recent notes.',
                },
                {
                    slug: 'appt-post-session-notes',
                    title: 'Post-session notes',
                    body: 'Write session notes for [client name] based on: [short description of what was discussed].',
                },
                {
                    slug: 'appt-recheck-reminder',
                    title: 'Re-check reminder',
                    body: 'Draft a re-check reminder message for [client name].',
                },
                {
                    slug: 'appt-clients-without',
                    title: 'Clients without an appointment',
                    body: 'Which clients have not booked an appointment in the last 2 months?',
                },
                {
                    slug: 'appt-end-of-day',
                    title: 'End-of-day summary',
                    body: 'At the end of the day, give me a summary: what happened, what is left, what to schedule tomorrow.',
                },
            ],
        },

        athlete: {
            label: 'Client profile',
            items: [
                {
                    slug: 'athlete-profile-summary',
                    title: 'Profile summary',
                    body: 'Show me a summary of the client\'s profile: health, medication, goals.',
                },
                {
                    slug: 'athlete-questionnaire-analysis',
                    title: 'Questionnaire analysis',
                    body: 'Analyze the client\'s questionnaire and highlight the points that matter for the diet plan.',
                },
                {
                    slug: 'athlete-dietary-warnings',
                    title: 'Dietary warnings',
                    body: 'Based on the client\'s health history, what dietary adjustments are needed?',
                },
                {
                    slug: 'athlete-meds-nutrition',
                    title: 'Medication & nutrition',
                    body: 'Based on the medication the client takes, does it affect their nutrition? What should I watch out for?',
                },
                {
                    slug: 'athlete-full-file-read',
                    title: 'Full file read',
                    body: 'Read all the client\'s file data and give me an executive summary.',
                },
                {
                    slug: 'athlete-risk-detection',
                    title: 'Risk detection',
                    body: 'Based on the client\'s age, conditions and medication, what are the main dietary contraindications?',
                },
                {
                    slug: 'athlete-update-notes',
                    title: 'Update notes',
                    body: 'Based on the latest session, write an updated note for the client\'s file.',
                },
                {
                    slug: 'athlete-history',
                    title: 'Client change history',
                    body: 'Show me how the client has progressed from day one until today: measurements, notes and goals.',
                },
                {
                    slug: 'athlete-drug-nutrient',
                    title: 'Drug-nutrient interactions',
                    body: 'Based on the client\'s medication, are there nutrients I should boost or avoid?',
                },
                {
                    slug: 'athlete-letter',
                    title: 'Client document (.docx)',
                    body: 'Create a professional Word document (.docx) in English with a full analysis of the client\'s profile, suitable to hand directly to them. Include: client details, medical history, medication, nutritional assessment, habits, exercise, goals and first-session questions.',
                },
            ],
        },

        athleteMeasurements: {
            label: 'Measurements',
            items: [
                {
                    slug: 'measurements-weight-progress',
                    title: 'Weight progress',
                    body: 'Show me the client\'s measurement progress: weight, fat, muscle mass.',
                },
                {
                    slug: 'measurements-compare',
                    title: 'Compare measurements',
                    body: 'Compare the client\'s measurements from the first check-up to the latest.',
                },
                {
                    slug: 'measurements-progress-note',
                    title: 'Progress note',
                    body: 'Based on the client\'s measurements, write a progress note.',
                },
                {
                    slug: 'measurements-body-composition',
                    title: 'Body composition analysis',
                    body: 'How have fat vs. muscle mass evolved for the client over the last 3 months?',
                },
                {
                    slug: 'measurements-appointment-summary',
                    title: 'Summary for appointment',
                    body: 'Prepare a summary of the client\'s latest measurements for the next appointment.',
                },
                {
                    slug: 'measurements-visualize',
                    title: 'Visualize progress',
                    body: 'Give me a clear summary of the client\'s progress that I can show them in the session, based on all the data.',
                },
                {
                    slug: 'measurements-rate',
                    title: 'Loss/gain rate',
                    body: 'Is the client\'s weight-change rate healthy and safe based on the latest measurements?',
                },
            ],
        },

        athleteMealLog: {
            label: 'Meal logging',
            items: [
                {
                    slug: 'meallog-evaluation',
                    title: 'Logs evaluation',
                    body: 'Evaluate the last 2 weeks of logs for the client and tell me what they should watch out for to improve nutrition, compliance and Mediterranean diet score, and give me 5 key goals for improvement.',
                },
                {
                    slug: 'meallog-plan-compliance',
                    title: 'Plan compliance',
                    body: 'Assess whether the client is following the latest plan based on their logged meals.',
                },
                {
                    slug: 'meallog-timing-pattern',
                    title: 'Timing pattern',
                    body: 'Analyze the client\'s meal timing: skipped meals, irregular hours, gaps.',
                },
                {
                    slug: 'meallog-skipped-meals',
                    title: 'Skipped meals',
                    body: 'Which meals does the client skip most often based on their logs?',
                },
                {
                    slug: 'meallog-behavior-note',
                    title: 'Eating-behavior note',
                    body: 'Based on the meal logs, write a note on the client\'s eating behavior.',
                },
                {
                    slug: 'meallog-deviations',
                    title: 'Deviation detection',
                    body: 'Find days when the client deviated significantly from the plan: what did they eat instead?',
                },
                {
                    slug: 'meallog-protein-analysis',
                    title: 'Protein analysis',
                    body: 'Does the client meet their daily protein target based on their logs? Where do they fall short?',
                },
                {
                    slug: 'meallog-peak-eating',
                    title: 'When do they eat most?',
                    body: 'At what time/meal of the day does the client consume the most calories?',
                },
                {
                    slug: 'meallog-week-compare',
                    title: 'Week comparison',
                    body: 'Compare the client\'s meal logs this week vs last week: improvement or regression?',
                },
                {
                    slug: 'meallog-real-vs-emotional',
                    title: 'Real vs emotional hunger',
                    body: 'Based on the client\'s meal and feelings logs, distinguish when they eat from real hunger vs emotional hunger.',
                },
                {
                    slug: 'meallog-emotional-patterns',
                    title: 'Emotional-eating patterns',
                    body: 'Which emotions are most often linked to the client\'s meals or snacks? (e.g. stress, boredom, joy)',
                },
                {
                    slug: 'meallog-overeating-triggers',
                    title: 'Overeating triggers',
                    body: 'Based on the logs, identify which emotions or times trigger overeating for the client.',
                },
                {
                    slug: 'meallog-mood-choices',
                    title: 'Mood-choice link',
                    body: 'How do the client\'s food choices change depending on the mood they have logged?',
                },
                {
                    slug: 'meallog-emotional-strategies',
                    title: 'Emotional-hunger strategies',
                    body: 'Based on the client\'s emotional-hunger patterns, suggest strategies for me to discuss with them in the session.',
                },
            ],
        },

        dietPlan: {
            label: 'Diet plan',
            items: [
                {
                    slug: 'dietplan-new-personalized',
                    title: 'New personalized plan',
                    preamble: 'You are an experienced Dietitian-Nutritionist.',
                    body: 'Create a personalized 7-day diet plan, 5 meals/day, for the client with the following targets:\n• Energy: 2000 kcal ± 10%\n• Protein: 120g ± 10%\n• Carbs: 45% ± 5%\n• Fat: 30% ± 5%\nRespect the contraindications from their profile. Include variety of meals each day. After creating it, check each day and automatically fix any day that falls outside the range for any macronutrient.\nFor breakfasts, include at least 3 different options. For main meals use a Mediterranean distribution for lunch, and for dinner either the same as lunch or something light with at least 3 different options, all adjusted to macronutrient and energy needs. A good weekly distribution is to base each day on chicken, legumes, olive-oil dishes (ladera), meat, fish, and pasta. Also make sure each meal is well balanced in energy and macros: one breakfast cannot have 20g protein and another 10g. In terms of macros and energy, each lunch, breakfast, snack and dinner should differ by at most 10% from one another.',
                },
                {
                    slug: 'dietplan-conditions',
                    title: 'Plan for conditions',
                    body: 'Build a 7-day plan for the client, suitable for high cholesterol and blood pressure. Respect the contraindications from their profile. Include variety of meals each day. After creating it, check each day and automatically fix any day that falls outside the range for any macronutrient. Keep the macronutrients of each meal uniform, with a maximum deviation of ± 10% between them.',
                },
                {
                    slug: 'dietplan-trial-3day',
                    title: 'Trial 3-day plan',
                    body: 'Create a 3-day trial plan with 4 meals for the client: 1800 kcal, 100-120g protein. After creating it, check each day and automatically fix any day that falls outside the range for any macronutrient.',
                },
                {
                    slug: 'dietplan-food-swap',
                    title: 'Food swap',
                    body: 'In the client\'s plan, what food can I use instead of [food] with similar calories and protein?',
                },
                {
                    slug: 'dietplan-vegan',
                    title: 'Veggie / vegan plan',
                    body: 'Create a weekly vegan plan for the client with sufficient protein: 1800 kcal. For breakfasts, include at least 3 different options. Make sure each meal is well balanced in energy and macros: one breakfast cannot have 20g protein and another 10g. In terms of macros and energy, each lunch, breakfast, snack and dinner should differ by at most 10% from one another. Respect the contraindications from their profile. Include variety of meals each day. After creating it, check each day and automatically fix any day that falls outside the range for any macronutrient.',
                },
                {
                    slug: 'dietplan-preferences',
                    title: 'Preference-based plan',
                    body: 'Build a plan for the client using the foods they said they like and avoiding the ones they dislike. Respect the contraindications from their profile. Include variety of meals each day. After creating it, check each day and automatically fix any day that falls outside the range for any macronutrient.',
                },
                {
                    slug: 'dietplan-alternatives',
                    title: 'Alternatives for balance',
                    body: 'In the client\'s plan, give me 3 alternatives for each meal so they do not get bored.',
                },
                {
                    slug: 'dietplan-personalized-full',
                    title: 'Personalized plan (full)',
                    preamble: 'You are an experienced Dietitian-Nutritionist.',
                    body: 'Create a personalized 7-day diet plan, 5 meals per day, for the client, using exclusively foods from the database.\n\nDAILY TARGETS\n• Energy: 2000 kcal ±10%\n• Protein: 120 g ±10%\n• Carbs: 45% ±5%\n• Fat: 30% ±5%\n\nMANDATORY CONSTRAINTS\n1. Take into account all contraindications, allergies, conditions, medication, dietary preferences and goals from the client profile.\n2. Include variety of meals and foods throughout the week.\n3. Follow a Mediterranean Diet model.\n4. Avoid excessive repetition of the same meals.\n5. Use realistic portions and food combinations.\n6. Use real foods from the database, not meal notes.\n\nBREAKFAST STRUCTURE\nCreate at least 3 different breakfast types that rotate during the week.\n\nWEEKLY MAIN-MEAL DISTRIBUTION\n• 2 days chicken\n• 1 day fish\n• 1 day legumes\n• 1 day olive-oil dish (ladero)\n• 1 day red meat\n• 1 day pasta or a grain-based composite dish\n\nDINNERS\nDinners should be either a variation of lunch or a lighter high-protein alternative. Include at least 3 different dinner types during the week.\n\nMEAL UNIFORMITY\nThe energy and macronutrient distribution between corresponding meals must be stable, with a maximum allowed deviation of ±10% across all meals.\nExample: if one breakfast provides 20g of protein, no other breakfast may provide less than 18g or more than 22g.\n\nINDICATIVE ENERGY DISTRIBUTION PER MEAL\n• Breakfast: 25% of daily energy\n• Mid-morning: 10%\n• Lunch: 30%\n• Afternoon: 10%\n• Dinner: 25%\nApply the same logic to macronutrients.\n\nMANDATORY QUALITY CHECK\nAfter creating the diet plan:\n1. Calculate for each day: kcal, protein, carbs, fat.\n2. Compare the results against the targets.\n3. If any day is outside the targets: automatically adjust quantities or foods, recalculate and re-check.\n4. Repeat the process until all days are within the targets.',
                },
            ],
        },

        athleteList: {
            label: 'Client list',
            items: [],
        },

        generic: {
            label: '',
            items: [],
        },
    },
};
